/**
 * Standard OBD-II service 0x01 PID table.
 *
 * Every entry declares how many data bytes to expect (so multi-PID responses
 * can be walked byte by byte), which signals it produces, and how fast it is
 * worth polling. Byte layouts follow SAE J1979 / ISO 15031-5.
 */
import type { Decoded, DisplayHints, PidDef, SignalDef } from '../types.js';

// ---------------------------------------------------------------- byte helpers

const b = (d: Uint8Array, i = 0): number => d[i] ?? 0;
const u16 = (d: Uint8Array, i = 0): number => ((d[i] ?? 0) << 8) | (d[i + 1] ?? 0);

/** A*100/255 -- the ubiquitous "percent of full scale" encoding. */
const pct = (v: number): number => (v * 100) / 255;
/** Fuel-trim style signed percent, -100 .. +99.2. */
const trim = (v: number): number => v / 1.28 - 100;
/** Temperatures are almost always offset by 40 degrees. */
const degC = (v: number): number => v - 40;

// ------------------------------------------------------------ tiny DSL helpers

type SigInit = Partial<DisplayHints> &
  Pick<SignalDef, 'key' | 'name' | 'short' | 'unit' | 'category'> &
  Partial<Pick<SignalDef, 'description' | 'enumLabels' | 'bitLabels'>>;

const S = (i: SigInit): SignalDef => ({
  min: 0,
  max: 100,
  decimals: 1,
  betterWhen: 'neutral',
  ...i,
});

const P = (
  pid: number,
  bytes: number,
  signals: SignalDef[],
  decode: (d: Uint8Array) => Decoded,
  defaultIntervalMs: number,
  batchable = true,
): PidDef => ({ mode: 0x01, pid, bytes, signals, decode, defaultIntervalMs, batchable });

/** Single-signal PID -- by far the common case. */
const P1 = (
  pid: number,
  bytes: number,
  s: SigInit,
  f: (d: Uint8Array) => number | undefined,
  defaultIntervalMs: number,
  batchable = true,
): PidDef => {
  const sg = S(s);
  return P(pid, bytes, [sg], (d) => ({ [sg.key]: f(d) }), defaultIntervalMs, batchable);
};

/** Cadence tiers in milliseconds. The scheduler treats these as a target only. */
export const RATE = {
  realtime: 50, // things you watch move: rpm, throttle, speed, MAF
  fast: 100,
  normal: 250,
  slow: 1000,
  lazy: 5000,
  rare: 30000, // odometer, tank level, distance counters
} as const;

// ------------------------------------------------- repeated sensor bank groups

/** 0x14..0x1B -- narrowband O2: voltage plus that sensor's short-term trim. */
function o2Voltage(pid: number, n: number): PidDef {
  const v = S({
    key: 'o2_s' + n + '_voltage', name: 'O2 Sensor ' + n + ' Voltage', short: 'O2 S' + n + ' V',
    unit: 'V', category: 'emissions', min: 0, max: 1.275, decimals: 3,
  });
  const t = S({
    key: 'o2_s' + n + '_trim', name: 'O2 Sensor ' + n + ' Short Term Fuel Trim', short: 'O2 S' + n + ' STFT',
    unit: '%', category: 'fuel', min: -25, max: 25, decimals: 1,
  });
  return P(pid, 2, [v, t], (d) => ({
    [v.key]: b(d, 0) / 200,
    // 0xFF in byte B means this sensor is not used for trim.
    [t.key]: b(d, 1) === 0xff ? undefined : trim(b(d, 1)),
  }), RATE.normal);
}

/** 0x24..0x2B -- wideband O2 expressed as lambda plus sensor voltage. */
function o2Lambda(pid: number, n: number): PidDef {
  const l = S({
    key: 'o2_s' + n + '_lambda', name: 'O2 Sensor ' + n + ' Lambda', short: 'O2 S' + n + ' La',
    unit: 'lambda', category: 'emissions', min: 0.7, max: 1.3, decimals: 3,
    description: 'Air/fuel equivalence ratio. 1.00 is stoichiometric.',
  });
  const v = S({
    key: 'o2_s' + n + '_wb_voltage', name: 'O2 Sensor ' + n + ' Voltage (wideband)', short: 'O2 S' + n + ' Vwb',
    unit: 'V', category: 'emissions', min: 0, max: 8, decimals: 3,
  });
  return P(pid, 4, [l, v], (d) => ({
    [l.key]: (2 / 65536) * u16(d, 0),
    [v.key]: (8 / 65536) * u16(d, 2),
  }), RATE.normal);
}

/** 0x34..0x3B -- wideband O2 as lambda plus pump current. */
function o2LambdaCurrent(pid: number, n: number): PidDef {
  const l = S({
    key: 'o2_s' + n + '_lambda_i', name: 'O2 Sensor ' + n + ' Lambda (current)', short: 'O2 S' + n + ' Lai',
    unit: 'lambda', category: 'emissions', min: 0.7, max: 1.3, decimals: 3,
  });
  const i = S({
    key: 'o2_s' + n + '_current', name: 'O2 Sensor ' + n + ' Current', short: 'O2 S' + n + ' I',
    unit: 'mA', category: 'emissions', min: -128, max: 128, decimals: 2,
  });
  return P(pid, 4, [l, i], (d) => ({
    [l.key]: (2 / 65536) * u16(d, 0),
    [i.key]: u16(d, 2) / 256 - 128,
  }), RATE.normal);
}

/** 0x3C..0x3F -- catalyst temperatures, one PID per bank/sensor pair. */
function catTemp(pid: number, bank: number, sensor: number): PidDef {
  return P1(pid, 2, {
    key: 'cat_temp_b' + bank + 's' + sensor,
    name: 'Catalyst Temperature Bank ' + bank + ' Sensor ' + sensor,
    short: 'Cat B' + bank + 'S' + sensor,
    unit: '°C', category: 'temperature', min: 0, max: 1000, decimals: 0,
  }, (d) => u16(d) / 10 - 40, RATE.lazy);
}

/**
 * 0x78/0x79 and 0x7C -- four temperature sensors behind a support bitmask in
 * byte A, then four big-endian pairs scaled by 10 with the usual -40 offset.
 */
function quadTemp(pid: number, prefix: string, name: string, cat: SignalDef['category']): PidDef {
  const sigs = [1, 2, 3, 4].map((n) => S({
    key: prefix + '_' + n, name: name + ' Sensor ' + n, short: prefix.toUpperCase() + ' ' + n,
    unit: '°C', category: cat, min: 0, max: 900, decimals: 0,
  }));
  return P(pid, 9, sigs, (d) => {
    const support = b(d, 0);
    const out: Decoded = {};
    sigs.forEach((s, idx) => {
      out[s.key] = (support & (1 << idx)) === 0 ? undefined : u16(d, 1 + idx * 2) / 10 - 40;
    });
    return out;
  }, RATE.slow, false);
}

// ----------------------------------------------------------- the table, 0x00 +

const TABLE: PidDef[] = [
  // 0x00/0x20/0x40/... are support bitmaps. Discovery reads them directly, so
  // they carry no signals and must never ride in a multi-PID request.
  P(0x00, 4, [], () => ({}), RATE.rare, false),

  P(0x01, 4, [
    S({ key: 'mil_on', name: 'Malfunction Indicator Lamp', short: 'MIL', unit: '', category: 'status', min: 0, max: 1, decimals: 0, betterWhen: 'lower' }),
    S({ key: 'dtc_count', name: 'Stored Trouble Codes', short: 'DTCs', unit: '', category: 'status', min: 0, max: 20, decimals: 0, betterWhen: 'lower' }),
  ], (d) => ({
    mil_on: (b(d, 0) & 0x80) !== 0 ? 1 : 0,
    dtc_count: b(d, 0) & 0x7f,
  }), RATE.lazy),

  P1(0x03, 2, {
    key: 'fuel_system_status', name: 'Fuel System 1 Status', short: 'Fuel Sys', unit: '',
    category: 'fuel', min: 0, max: 16, decimals: 0,
    enumLabels: {
      0: 'Off', 1: 'Open loop (cold)', 2: 'Closed loop', 4: 'Open loop (load/decel)',
      8: 'Open loop (fault)', 16: 'Closed loop with fault',
    },
  }, (d) => b(d, 0), RATE.slow),

  P1(0x04, 1, {
    key: 'engine_load', name: 'Calculated Engine Load', short: 'Load', unit: '%',
    category: 'engine', min: 0, max: 100, decimals: 1, warnAbove: 90,
    description: 'Current airflow as a fraction of peak airflow at this RPM.',
  }, (d) => pct(b(d)), RATE.fast),

  P1(0x05, 1, {
    key: 'coolant_temp', name: 'Engine Coolant Temperature', short: 'Coolant', unit: '°C',
    category: 'temperature', min: -40, max: 130, decimals: 0, warnAbove: 105,
  }, (d) => degC(b(d)), RATE.slow),

  P1(0x06, 1, { key: 'stft_b1', name: 'Short Term Fuel Trim Bank 1', short: 'STFT B1', unit: '%', category: 'fuel', min: -25, max: 25, decimals: 1 }, (d) => trim(b(d)), RATE.normal),
  P1(0x07, 1, { key: 'ltft_b1', name: 'Long Term Fuel Trim Bank 1', short: 'LTFT B1', unit: '%', category: 'fuel', min: -25, max: 25, decimals: 1 }, (d) => trim(b(d)), RATE.slow),
  P1(0x08, 1, { key: 'stft_b2', name: 'Short Term Fuel Trim Bank 2', short: 'STFT B2', unit: '%', category: 'fuel', min: -25, max: 25, decimals: 1 }, (d) => trim(b(d)), RATE.normal),
  P1(0x09, 1, { key: 'ltft_b2', name: 'Long Term Fuel Trim Bank 2', short: 'LTFT B2', unit: '%', category: 'fuel', min: -25, max: 25, decimals: 1 }, (d) => trim(b(d)), RATE.slow),

  P1(0x0a, 1, { key: 'fuel_pressure', name: 'Fuel Pressure (gauge)', short: 'Fuel P', unit: 'kPa', category: 'fuel', min: 0, max: 765, decimals: 0 }, (d) => b(d) * 3, RATE.slow),

  P1(0x0b, 1, {
    key: 'map', name: 'Intake Manifold Absolute Pressure', short: 'MAP', unit: 'kPa',
    category: 'air', min: 0, max: 255, decimals: 0,
    description: 'Absolute, not gauge. Below ~101 kPa is vacuum, above it is boost.',
  }, (d) => b(d), RATE.realtime),

  P1(0x0c, 2, {
    key: 'rpm', name: 'Engine Speed', short: 'RPM', unit: 'rpm',
    category: 'engine', min: 0, max: 7000, decimals: 0,
  }, (d) => u16(d) / 4, RATE.realtime),

  P1(0x0d, 1, {
    key: 'speed', name: 'Vehicle Speed', short: 'Speed', unit: 'km/h',
    category: 'vehicle', min: 0, max: 240, decimals: 0,
  }, (d) => b(d), RATE.realtime),

  P1(0x0e, 1, {
    key: 'timing_advance', name: 'Timing Advance', short: 'Timing', unit: '°',
    category: 'engine', min: -64, max: 64, decimals: 1,
    description: 'Degrees before TDC on cylinder 1. Retard under load can indicate knock.',
  }, (d) => b(d) / 2 - 64, RATE.fast),

  P1(0x0f, 1, { key: 'intake_temp', name: 'Intake Air Temperature', short: 'IAT', unit: '°C', category: 'temperature', min: -40, max: 100, decimals: 0 }, (d) => degC(b(d)), RATE.slow),

  P1(0x10, 2, {
    key: 'maf', name: 'Mass Air Flow Rate', short: 'MAF', unit: 'g/s',
    category: 'air', min: 0, max: 250, decimals: 2,
    description: 'Air mass entering the engine. The basis for real fuel-flow maths.',
  }, (d) => u16(d) / 100, RATE.realtime),

  P1(0x11, 1, {
    key: 'throttle', name: 'Throttle Position', short: 'Throttle', unit: '%',
    category: 'engine', min: 0, max: 100, decimals: 1,
    description: 'Absolute throttle plate angle, including the idle offset.',
  }, (d) => pct(b(d)), RATE.realtime),

  P1(0x12, 1, {
    key: 'secondary_air_status', name: 'Commanded Secondary Air Status', short: 'Sec Air', unit: '',
    category: 'emissions', min: 0, max: 8, decimals: 0,
    enumLabels: { 1: 'Upstream', 2: 'Downstream of cat', 4: 'From outside/off', 8: 'Pump commanded on' },
  }, (d) => b(d), RATE.rare),

  P1(0x1f, 2, {
    key: 'run_time', name: 'Run Time Since Engine Start', short: 'Run Time', unit: 's',
    category: 'status', min: 0, max: 65535, decimals: 0,
  }, (d) => u16(d), RATE.slow),

  // ------------------------------------------------------ mode 01: 0x21 - 0x3F

  P1(0x21, 2, { key: 'distance_mil', name: 'Distance Travelled With MIL On', short: 'Dist MIL', unit: 'km', category: 'status', min: 0, max: 65535, decimals: 0, betterWhen: 'lower' }, (d) => u16(d), RATE.rare),
  P1(0x22, 2, { key: 'fuel_rail_pressure_vac', name: 'Fuel Rail Pressure (rel. to manifold)', short: 'Rail P rel', unit: 'kPa', category: 'fuel', min: 0, max: 5178, decimals: 0 }, (d) => u16(d) * 0.079, RATE.slow),
  P1(0x23, 2, {
    key: 'fuel_rail_pressure', name: 'Fuel Rail Gauge Pressure', short: 'Rail P', unit: 'kPa',
    category: 'diesel', min: 0, max: 200000, decimals: 0,
    description: 'Direct-injection rail pressure. Common on diesel and GDI petrol.',
  }, (d) => u16(d) * 10, RATE.normal),

  P1(0x2c, 1, { key: 'egr_commanded', name: 'Commanded EGR', short: 'EGR cmd', unit: '%', category: 'emissions', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.normal),
  P1(0x2d, 1, { key: 'egr_error', name: 'EGR Error', short: 'EGR err', unit: '%', category: 'emissions', min: -100, max: 100, decimals: 1 }, (d) => trim(b(d)), RATE.normal),
  P1(0x2e, 1, { key: 'evap_purge', name: 'Commanded Evaporative Purge', short: 'Evap', unit: '%', category: 'emissions', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.slow),
  P1(0x2f, 1, {
    key: 'fuel_level', name: 'Fuel Tank Level', short: 'Fuel', unit: '%',
    category: 'fuel', min: 0, max: 100, decimals: 1, warnBelow: 10, betterWhen: 'higher',
  }, (d) => pct(b(d)), RATE.rare),

  P1(0x30, 1, { key: 'warmups_since_clear', name: 'Warm-ups Since Codes Cleared', short: 'Warmups', unit: '', category: 'status', min: 0, max: 255, decimals: 0 }, (d) => b(d), RATE.rare),
  P1(0x31, 2, { key: 'distance_since_clear', name: 'Distance Since Codes Cleared', short: 'Dist clr', unit: 'km', category: 'status', min: 0, max: 65535, decimals: 0 }, (d) => u16(d), RATE.rare),
  P1(0x32, 2, { key: 'evap_vapor_pressure', name: 'Evap System Vapor Pressure', short: 'Evap P', unit: 'Pa', category: 'emissions', min: -8192, max: 8192, decimals: 1 }, (d) => { const v = u16(d); return (v > 32767 ? v - 65536 : v) / 4; }, RATE.lazy),
  P1(0x33, 1, {
    key: 'baro', name: 'Absolute Barometric Pressure', short: 'Baro', unit: 'kPa',
    category: 'air', min: 0, max: 255, decimals: 0,
    description: 'Ambient pressure. Subtract from MAP to get true boost.',
  }, (d) => b(d), RATE.lazy),

  // ------------------------------------------------------ mode 01: 0x41 - 0x5F

  P1(0x42, 2, {
    key: 'module_voltage', name: 'Control Module Voltage', short: 'Battery', unit: 'V',
    category: 'electrical', min: 8, max: 16, decimals: 2, warnBelow: 11.8,
    description: 'ECU supply voltage. A good proxy for battery and alternator health.',
  }, (d) => u16(d) / 1000, RATE.slow),

  P1(0x43, 2, {
    key: 'absolute_load', name: 'Absolute Load Value', short: 'Abs Load', unit: '%',
    category: 'engine', min: 0, max: 250, decimals: 1,
    description: 'Unlike calculated load this is not normalised by RPM, so it tracks torque.',
  }, (d) => (u16(d) * 100) / 255, RATE.fast),

  P1(0x44, 2, {
    key: 'lambda_commanded', name: 'Commanded Air-Fuel Equivalence Ratio', short: 'Lambda cmd', unit: 'lambda',
    category: 'fuel', min: 0.7, max: 1.3, decimals: 3,
    description: 'What the ECU is aiming for. Below 1.00 is enrichment under load.',
  }, (d) => (2 / 65536) * u16(d), RATE.fast),

  P1(0x45, 1, { key: 'throttle_rel', name: 'Relative Throttle Position', short: 'Throttle rel', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1, description: 'Throttle angle with the learned closed position subtracted -- this is the one that reads 0% at idle.' }, (d) => pct(b(d)), RATE.realtime),
  P1(0x46, 1, { key: 'ambient_temp', name: 'Ambient Air Temperature', short: 'Ambient', unit: '°C', category: 'temperature', min: -40, max: 60, decimals: 0 }, (d) => degC(b(d)), RATE.lazy),
  P1(0x47, 1, { key: 'throttle_b', name: 'Absolute Throttle Position B', short: 'Throttle B', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.normal),
  P1(0x48, 1, { key: 'throttle_c', name: 'Absolute Throttle Position C', short: 'Throttle C', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.normal),
  P1(0x49, 1, {
    key: 'pedal_d', name: 'Accelerator Pedal Position D', short: 'Pedal D', unit: '%',
    category: 'engine', min: 0, max: 100, decimals: 1,
    description: 'Your right foot, before the ECU decides what to do with it.',
  }, (d) => pct(b(d)), RATE.realtime),
  P1(0x4a, 1, { key: 'pedal_e', name: 'Accelerator Pedal Position E', short: 'Pedal E', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.fast),
  P1(0x4b, 1, { key: 'pedal_f', name: 'Accelerator Pedal Position F', short: 'Pedal F', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.fast),
  P1(0x4c, 1, { key: 'throttle_actuator', name: 'Commanded Throttle Actuator', short: 'Throttle act', unit: '%', category: 'engine', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.fast),
  P1(0x4d, 2, { key: 'time_mil_on', name: 'Time Run With MIL On', short: 'MIL time', unit: 'min', category: 'status', min: 0, max: 65535, decimals: 0, betterWhen: 'lower' }, (d) => u16(d), RATE.rare),
  P1(0x4e, 2, { key: 'time_since_clear', name: 'Time Since Codes Cleared', short: 'Since clr', unit: 'min', category: 'status', min: 0, max: 65535, decimals: 0 }, (d) => u16(d), RATE.rare),

  P1(0x51, 1, {
    key: 'fuel_type_code', name: 'Fuel Type', short: 'Fuel type', unit: '',
    category: 'status', min: 0, max: 23, decimals: 0,
    description: 'Declared by the ECU. Drives the consumption maths when present.',
    enumLabels: {
      0: 'Not available', 1: 'Petrol', 2: 'Methanol', 3: 'Ethanol', 4: 'Diesel', 5: 'LPG',
      6: 'CNG', 7: 'Propane', 8: 'Electric', 9: 'Bifuel petrol', 10: 'Bifuel methanol',
      11: 'Bifuel ethanol', 12: 'Bifuel LPG', 13: 'Bifuel CNG', 14: 'Bifuel propane',
      15: 'Bifuel electric', 16: 'Bifuel electric+combustion', 17: 'Hybrid petrol',
      18: 'Hybrid ethanol', 19: 'Hybrid diesel', 20: 'Hybrid electric',
      21: 'Hybrid mixed', 22: 'Hybrid regenerative', 23: 'Bifuel diesel',
    },
  }, (d) => b(d), RATE.rare),

  P1(0x52, 1, { key: 'ethanol_pct', name: 'Ethanol Fuel Percentage', short: 'Ethanol', unit: '%', category: 'fuel', min: 0, max: 100, decimals: 1 }, (d) => pct(b(d)), RATE.rare),
  P1(0x53, 2, { key: 'evap_abs_pressure', name: 'Absolute Evap System Vapor Pressure', short: 'Evap abs', unit: 'kPa', category: 'emissions', min: 0, max: 328, decimals: 2 }, (d) => u16(d) / 200, RATE.lazy),
  P1(0x55, 2, { key: 'stft_o2_b1', name: 'Short Term Secondary O2 Trim Bank 1', short: 'ST O2 B1', unit: '%', category: 'fuel', min: -100, max: 100, decimals: 1 }, (d) => trim(b(d, 0)), RATE.slow),
  P1(0x56, 2, { key: 'ltft_o2_b1', name: 'Long Term Secondary O2 Trim Bank 1', short: 'LT O2 B1', unit: '%', category: 'fuel', min: -100, max: 100, decimals: 1 }, (d) => trim(b(d, 0)), RATE.slow),
  P1(0x57, 2, { key: 'stft_o2_b2', name: 'Short Term Secondary O2 Trim Bank 2', short: 'ST O2 B2', unit: '%', category: 'fuel', min: -100, max: 100, decimals: 1 }, (d) => trim(b(d, 0)), RATE.slow),
  P1(0x58, 2, { key: 'ltft_o2_b2', name: 'Long Term Secondary O2 Trim Bank 2', short: 'LT O2 B2', unit: '%', category: 'fuel', min: -100, max: 100, decimals: 1 }, (d) => trim(b(d, 0)), RATE.slow),

  P1(0x59, 2, { key: 'fuel_rail_abs_pressure', name: 'Fuel Rail Absolute Pressure', short: 'Rail P abs', unit: 'kPa', category: 'diesel', min: 0, max: 655350, decimals: 0 }, (d) => u16(d) * 10, RATE.normal),
  P1(0x5a, 1, {
    key: 'pedal_rel', name: 'Relative Accelerator Pedal Position', short: 'Pedal rel', unit: '%',
    category: 'engine', min: 0, max: 100, decimals: 1,
  }, (d) => pct(b(d)), RATE.realtime),
  P1(0x5b, 1, { key: 'hybrid_battery_life', name: 'Hybrid Battery Pack Remaining Life', short: 'HV Batt', unit: '%', category: 'hybrid', min: 0, max: 100, decimals: 1, betterWhen: 'higher' }, (d) => pct(b(d)), RATE.lazy),
  P1(0x5c, 1, {
    key: 'oil_temp', name: 'Engine Oil Temperature', short: 'Oil', unit: '°C',
    category: 'temperature', min: -40, max: 160, decimals: 0, warnAbove: 130,
  }, (d) => degC(b(d)), RATE.slow),
  P1(0x5d, 2, {
    key: 'injection_timing', name: 'Fuel Injection Timing', short: 'Inj timing', unit: '°',
    category: 'diesel', min: -210, max: 302, decimals: 2,
  }, (d) => u16(d) / 128 - 210, RATE.fast),
  P1(0x5e, 2, {
    key: 'fuel_rate_ecu', name: 'Engine Fuel Rate (from ECU)', short: 'Fuel rate', unit: 'L/h',
    category: 'fuel', min: 0, max: 60, decimals: 2, betterWhen: 'lower',
    description: 'Reported directly by the ECU. Preferred over MAF-derived flow when present.',
  }, (d) => u16(d) / 20, RATE.fast),

  // ------------------------------------------------------ mode 01: 0x61 - 0x7F

  P1(0x61, 1, { key: 'torque_demand', name: 'Driver Demand Engine Torque', short: 'Trq demand', unit: '%', category: 'engine', min: -125, max: 130, decimals: 0 }, (d) => b(d) - 125, RATE.fast),
  P1(0x62, 1, {
    key: 'torque_actual', name: 'Actual Engine Torque', short: 'Trq actual', unit: '%',
    category: 'engine', min: -125, max: 130, decimals: 0,
    description: 'Percent of the reference torque in PID 0x63. Together they give real Nm.',
  }, (d) => b(d) - 125, RATE.fast),
  P1(0x63, 2, {
    key: 'torque_reference', name: 'Engine Reference Torque', short: 'Trq ref', unit: 'Nm',
    category: 'engine', min: 0, max: 65535, decimals: 0,
  }, (d) => u16(d), RATE.rare),

  P1(0x6f, 3, { key: 'turbo_inlet_pressure', name: 'Turbocharger Compressor Inlet Pressure', short: 'Turbo in P', unit: 'kPa', category: 'turbo', min: 0, max: 255, decimals: 0 }, (d) => b(d, 1), RATE.normal, false),
  P1(0x73, 5, { key: 'exhaust_pressure', name: 'Exhaust Pressure', short: 'Exh P', unit: 'kPa', category: 'diesel', min: 0, max: 6553, decimals: 1 }, (d) => u16(d, 1) / 128, RATE.normal, false),
  P1(0x74, 5, {
    key: 'turbo_rpm', name: 'Turbocharger RPM', short: 'Turbo RPM', unit: 'rpm',
    category: 'turbo', min: 0, max: 200000, decimals: 0,
  }, (d) => u16(d, 1), RATE.fast, false),
  P1(0x77, 5, { key: 'charge_air_temp', name: 'Charge Air Cooler Temperature', short: 'CAC temp', unit: '°C', category: 'turbo', min: -40, max: 200, decimals: 0 }, (d) => u16(d, 1) / 10 - 40, RATE.slow, false),

  quadTemp(0x78, 'egt_b1', 'Exhaust Gas Temperature Bank 1', 'diesel'),
  quadTemp(0x79, 'egt_b2', 'Exhaust Gas Temperature Bank 2', 'diesel'),

  P(0x7a, 7, [
    S({ key: 'dpf_b1_inlet', name: 'DPF Bank 1 Inlet Pressure', short: 'DPF in', unit: 'kPa', category: 'diesel', min: 0, max: 500, decimals: 2 }),
    S({ key: 'dpf_b1_outlet', name: 'DPF Bank 1 Outlet Pressure', short: 'DPF out', unit: 'kPa', category: 'diesel', min: 0, max: 500, decimals: 2 }),
    S({
      key: 'dpf_b1_delta', name: 'DPF Bank 1 Differential Pressure', short: 'DPF dP', unit: 'kPa',
      category: 'diesel', min: 0, max: 100, decimals: 2, warnAbove: 30, betterWhen: 'lower',
      description: 'Rises as soot loads the filter. A regeneration should bring it back down.',
    }),
  ], (d) => ({
    dpf_b1_inlet: u16(d, 1) / 32 - 8192,
    dpf_b1_outlet: u16(d, 3) / 32 - 8192,
    dpf_b1_delta: u16(d, 5) / 32 - 8192,
  }), RATE.slow, false),

  quadTemp(0x7c, 'dpf_temp', 'DPF Temperature', 'diesel'),

  P1(0x9d, 4, { key: 'fuel_rate_gs', name: 'Engine Fuel Rate (mass)', short: 'Fuel g/s', unit: 'g/s', category: 'fuel', min: 0, max: 100, decimals: 2, betterWhen: 'lower' }, (d) => u16(d, 0) / 50, RATE.fast, false),
  P1(0x9e, 2, { key: 'exhaust_flow', name: 'Engine Exhaust Flow Rate', short: 'Exh flow', unit: 'kg/h', category: 'diesel', min: 0, max: 6553, decimals: 1 }, (d) => u16(d) / 10, RATE.normal),
  P1(0xa2, 2, { key: 'cylinder_fuel_rate', name: 'Cylinder Fuel Rate', short: 'Cyl fuel', unit: 'mg/stroke', category: 'diesel', min: 0, max: 2048, decimals: 2 }, (d) => u16(d) / 32, RATE.fast),
  P1(0xa6, 4, {
    key: 'odometer', name: 'Odometer', short: 'Odo', unit: 'km',
    category: 'vehicle', min: 0, max: 1000000, decimals: 1,
  }, (d) => (((d[0] ?? 0) * 0x1000000) + ((d[1] ?? 0) << 16) + ((d[2] ?? 0) << 8) + (d[3] ?? 0)) / 10, RATE.rare, false),
];

// Fill in the eight repeating O2 banks without eight copies of the same code.
for (let n = 1; n <= 8; n++) {
  TABLE.push(o2Voltage(0x13 + n, n));
  TABLE.push(o2Lambda(0x23 + n, n));
  TABLE.push(o2LambdaCurrent(0x33 + n, n));
}
TABLE.push(catTemp(0x3c, 1, 1), catTemp(0x3d, 2, 1), catTemp(0x3e, 1, 2), catTemp(0x3f, 2, 2));

// -------------------------------------------------------------------- indexes

/** Every PID definition, keyed by PID number within mode 0x01. */
export const PID_BY_NUMBER: ReadonlyMap<number, PidDef> = new Map(TABLE.map((p) => [p.pid, p]));

/** Every signal definition in the standard set, keyed by signal key. */
export const SIGNAL_BY_KEY: ReadonlyMap<string, SignalDef> = new Map(
  TABLE.flatMap((p) => p.signals).map((s) => [s.key, s]),
);

/** Which PID must be polled to obtain a given signal. */
export const PID_FOR_SIGNAL: ReadonlyMap<string, PidDef> = new Map(
  TABLE.flatMap((p) => p.signals.map((s) => [s.key, p] as const)),
);

/** The PID support bitmap PIDs, in the order discovery should walk them. */
export const SUPPORT_BITMAP_PIDS = [0x00, 0x20, 0x40, 0x60, 0x80, 0xa0, 0xc0] as const;

export const ALL_PIDS: readonly PidDef[] = TABLE;
